import React, { useRef, useState } from "react";
import {
  Upload,
  X,
  FileText,
  Image as ImageIcon,
  Paperclip,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ALLOWED_FILE_EXTENSIONS,
  MAX_FILE_ATTACHMENT_SIZE_BYTES,
} from "@api/shared-runtime/file-attachment";

const ACCEPT_ATTRIBUTE = ALLOWED_FILE_EXTENSIONS.join(",");
const IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

export interface LocalFile {
  id: string;
  file: File;
}

interface LocalFileInputProps {
  /** Files selected by the user (not yet uploaded) */
  files: LocalFile[];
  /** Called when files are added or removed */
  onFilesChange: (files: LocalFile[]) => void;
  /** Maximum number of files (default 10) */
  maxFiles?: number;
  /** Whether to allow multiple files (default true) */
  multiple?: boolean;
  /** Disable the input */
  disabled?: boolean;
  /** Compact mode for inline use */
  compact?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(type: string) {
  if (IMAGE_TYPES.has(type)) {
    return <ImageIcon size={14} className="text-blue-500" />;
  }
  return <FileText size={14} className="text-gray-500" />;
}

export function LocalFileInput({
  files,
  onFilesChange,
  maxFiles = 10,
  multiple = true,
  disabled = false,
  compact = false,
}: LocalFileInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const addFiles = (fileList: FileList | File[]) => {
    const remaining = maxFiles - files.length;
    const newFiles = Array.from(fileList)
      .slice(0, remaining)
      .filter((f) => f.size <= MAX_FILE_ATTACHMENT_SIZE_BYTES && f.size > 0)
      .map((file) => ({
        id: crypto.randomUUID(),
        file,
      }));
    if (newFiles.length > 0) {
      onFilesChange([...files, ...newFiles]);
    }
  };

  const removeFile = (id: string) => {
    onFilesChange(files.filter((f) => f.id !== id));
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
      e.target.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!disabled && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const canAddMore = files.length < maxFiles;

  return (
    <div className="space-y-2">
      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((localFile) => (
            <div
              key={localFile.id}
              className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-md border border-gray-100 text-sm"
            >
              {getFileIcon(localFile.file.type)}
              <span className="flex-1 min-w-0 truncate text-gray-700">
                {localFile.file.name}
              </span>
              <span className="text-xs text-gray-400 shrink-0">
                {formatFileSize(localFile.file.size)}
              </span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeFile(localFile.id)}
                  className="text-gray-300 hover:text-red-500 shrink-0 p-0.5"
                  aria-label="Remove file"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload area */}
      {canAddMore && !disabled && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTRIBUTE}
            multiple={multiple}
            onChange={handleFileInputChange}
            className="hidden"
          />

          {compact ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs h-8"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip size={12} className="mr-1.5" />
              Attach file{multiple ? "s" : ""}
            </Button>
          ) : (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-blue-400 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <Upload size={20} className="mx-auto mb-1.5 text-gray-400" />
              <p className="text-sm text-gray-500">
                Drop files here or{" "}
                <span className="text-blue-600 font-medium">browse</span>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                PDF, images, documents up to 20MB
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
